import { SUBJECT_LIBRARY_TAB_SLUG } from '@yandex-int/k-common/typings/index';
import cx from 'classnames';
import { PlusKit } from 'education-icons';
import { Button, SimpleText, useStaticUrls } from 'platform-components';
import { AppFactoryStore } from 'platform-components/src/components/app-factory/typings';
import { getWizardClassName } from 'platform-components/src/components/wizard/utils';
import { withRouterPath, InjectedProps as WithRouterPathProps } from 'platform-components/hocs';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { matchPath } from 'react-router-dom';
import ym from 'react-yandex-metrika';
import { Action, bindActionCreators, Dispatch } from 'redux';

import {
    BLOCK_CARDS_COUNT,
    LIBRARY_CONTENT_TYPE,
    SEARCH_PARAM_RELOAD_LESSON_ID,
    SEARCH_PARAM_RELOAD_SYMBOL,
} from 'common.components/constants';
// @ts-ignore
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import * as lessonViewSelectors from 'common.components/lesson-view.store/selectors';
import lessonActions from 'common.components/lesson/actions';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { LessonStore } from 'common.components/lesson/typings';
import { getIsInStudentStatsBundle } from 'utils/get-is-in-student-stats-bundle';
import { getSubjectFromPath } from 'utils/path';

interface StoreProps {
    problemsCount: number;
    isPreparedLesson: boolean;
    isAfterCopyingAsRNO: boolean;
    courseId: number | null;
    canAddProblemToLesson: boolean;
    clessonId: number | null;
}

interface DispatchProps {
    toggleLessonModal: typeof lessonViewActions.toggleModal;
    addLessonToCourseAsync: typeof lessonActions.addLessonToCourseAsync;
    getFullClessonRequest: typeof lessonActions.getFullClessonRequest;
}

export interface Props extends StoreProps, DispatchProps, WithRouterPathProps {}

export const AddProblemButton = (props: Props) => {
    const {
        getRouterPath,
        history,
        toggleLessonModal,
        location: { pathname },
        problemsCount,
        isPreparedLesson,
        isAfterCopyingAsRNO,
        addLessonToCourseAsync,
        getFullClessonRequest,
        courseId,
        match,
        canAddProblemToLesson,
        clessonId,
    } = props;
    const canAddMoreProblems = problemsCount < BLOCK_CARDS_COUNT;
    const { labLibrarySubject, labClassLibrarySubjectTab, labLibrarySubjectPromo } = useStaticUrls();
    const subject = match?.params?.subject;
    const tabSlug = match?.params?.tabSlug;

    const getShouldRedirect = useCallback(() => {
        const isInLibrary = matchPath(pathname, { path: labLibrarySubject, exact: false });
        const isNotOnMainTab =
            matchPath(pathname, { path: labClassLibrarySubjectTab, exact: false }) &&
            tabSlug !== SUBJECT_LIBRARY_TAB_SLUG.MAIN;
        const isOnPromoPage = matchPath(pathname, { path: labLibrarySubjectPromo, exact: true });

        return !isInLibrary || (isInLibrary && isNotOnMainTab) || isOnPromoPage;
    }, [pathname, labLibrarySubject, labClassLibrarySubjectTab, labLibrarySubjectPromo, tabSlug]);

    const handleAddCard = async () => {
        if (canAddMoreProblems) {
            toggleLessonModal(false);
            const shouldRedirect = getShouldRedirect();

            if (shouldRedirect) {
                const librarySubjectPath = getRouterPath(labClassLibrarySubjectTab, {
                    subject,
                    tabSlug: SUBJECT_LIBRARY_TAB_SLUG.MAIN,
                });

                if (getIsInStudentStatsBundle(pathname)) {
                    const symbol = encodeURIComponent(SEARCH_PARAM_RELOAD_SYMBOL);
                    // бандлы разные, переход через history.push невозможен
                    window.location.href = `${librarySubjectPath}?${[
                        SEARCH_PARAM_RELOAD_LESSON_ID,
                    ]}=${clessonId}${symbol}${courseId}`;
                } else {
                    history.push(librarySubjectPath);
                }
            }

            ym('reachGoal', 'teacher_lesson_creation_back_to_catalogue', {
                subject,
                contentType: isPreparedLesson
                    ? LIBRARY_CONTENT_TYPE.PREPARED_LESSON
                    : LIBRARY_CONTENT_TYPE.COURSE_LESSON,
                isCross: 0,
            });

            if (isPreparedLesson && courseId) {
                const newClessonId = (await addLessonToCourseAsync()) as unknown as number;
                getFullClessonRequest({ clessonId: newClessonId, courseId });
            }
        }
    };

    if (!canAddProblemToLesson) {
        return null;
    }

    const icon = canAddMoreProblems ? <PlusKit /> : undefined;
    const mix = isAfterCopyingAsRNO ? getWizardClassName('lesson-view__add-problem-button') : '';

    return (
        <Button
            className={cx(
                'lesson-view__add-problem-button lesson-view__header-button lesson-view__controll-button',
                mix
            )}
            disabled={!canAddMoreProblems}
            icon={icon}
            onClick={handleAddCard}
            view={Button.VIEW.SECONDARY}
        >
            <SimpleText id={canAddMoreProblems ? 'lessonView.addProblem' : 'lessonView.addProblem.limited'} />
        </Button>
    );
};

const mapStateToProps = <T extends LessonStore & AppFactoryStore>(storeState: T): StoreProps => {
    const subject = getSubjectFromPath();

    return {
        problemsCount: lessonSelectors.getLessonProblems(storeState).length,
        isPreparedLesson: lessonSelectors.getIsPreparedLesson(storeState),
        courseId: lessonSelectors.getLessonOrCurrentYearCourseId(storeState),
        isAfterCopyingAsRNO: lessonViewSelectors.getIsAfterCopyingAsRNO(storeState),
        canAddProblemToLesson: lessonViewSelectors.getCanAddProblemToLesson(storeState, { subject }),
        clessonId: lessonSelectors.getCLessonId(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => {
    return bindActionCreators(
        {
            toggleLessonModal: lessonViewActions.toggleModal,
            addLessonToCourseAsync: lessonActions.addLessonToCourseAsync,
            getFullClessonRequest: lessonActions.getFullClessonRequest,
        },
        dispatch
    );
};

AddProblemButton.contextTypes = {
    staticUrls: PropTypes.object.isRequired,
};

const wrapped = withRouterPath(connect(mapStateToProps, mapDispatchToProps)(AddProblemButton));
wrapped.displayName = 'AddProblemButton';

export default wrapped;
